import '@eliberty/design-system-react/main.css';

import { createRoot } from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';
import { ApolloProvider } from '@apollo/client';
import { ElibertyThemeProvider } from '@eliberty/design-system-react';
import { PostHogProvider } from 'posthog-js/react';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';

import { WidgetsInterface } from '~constants/WidgetsNames';
import getClient from '~graphql/getClient';
import { ErrorBoundary } from '~organisms';
import { CacheVersionProvider, ConfiguratorProvider, HostElementProvider } from '~providers';
import { store } from '~redux/store';
import { getWidgetsConfig } from '~utils';

import { PORTAL_HOST_ID, PORTAL_INNER_ID } from './modalPortal';
import { getLocale, loadTranslations } from './translationHelper';

const options = {
  api_host: 'https://dqvmlfjl0k03.cloudfront.net',
  ui_host: 'https://eu.i.posthog.com',
};

const persistor = persistStore(store);

/**
 * Initialize a widget's Shadow DOM, inject styles/theme, render the React tree,
 * and create the shared modal portal if needed.
 * Extracted so that both GenericWidgetElement and specialized elements (e.g. PurchaseWidgetElement)
 * can reuse the same setup logic.
 */
export const initWidgetShadowDom = (host: HTMLElement, widget: WidgetsInterface) => {
  const graphQlClient = getClient();
  if (!host.shadowRoot) {
    host.attachShadow({ mode: 'open' });
    host.style.cssText = 'all: initial';
    host.style.display = 'block';
  }

  const style = document.createElement('link');
  const scriptSrc = getWidgetsConfig('src');
  style.href = scriptSrc!.replace('bundle.js', 'main.css');
  style.rel = 'stylesheet';
  host.shadowRoot!.appendChild(style);

  const theme = getWidgetsConfig('data-theme') || 'default';

  const fontLinksToWait: HTMLLinkElement[] = [];

  const defaultFontLinkId = `default-fonts-${theme}`;
  const existingDefaultFontLink = document.getElementById(
    defaultFontLinkId,
  ) as HTMLLinkElement | null;
  if (!existingDefaultFontLink) {
    const fontLinkElement = document.createElement('link');
    fontLinkElement.id = defaultFontLinkId;
    fontLinkElement.rel = 'stylesheet';
    fontLinkElement.href = `https://static.eliberty.fr/Themes/default/assets/fonts.css`;
    document.head.appendChild(fontLinkElement);
    fontLinksToWait.push(fontLinkElement);
  } else if (!existingDefaultFontLink.sheet) {
    // Already in DOM but not yet parsed — wait for it
    fontLinksToWait.push(existingDefaultFontLink);
  }

  let variablesLinkElement: HTMLLinkElement | undefined;
  if (theme !== 'default') {
    const fontLinkThemeElement = document.createElement('link');
    fontLinkThemeElement.rel = 'stylesheet';
    fontLinkThemeElement.href = `https://static.eliberty.fr/Themes/${theme}/assets/fonts.css`;
    document.head.appendChild(fontLinkThemeElement);
    fontLinksToWait.push(fontLinkThemeElement);

    variablesLinkElement = document.createElement('link');
    variablesLinkElement.rel = 'stylesheet';
    variablesLinkElement.href = `https://static.eliberty.fr/Themes/${theme}/v1.0.2/variables.css`;
    host.shadowRoot!.appendChild(variablesLinkElement);
  }

  const cloneIconsLinkToShadowRoots = (iconsLink: HTMLLinkElement) => {
    host.shadowRoot!.appendChild(iconsLink.cloneNode());
    const portalShadowRoot = document.getElementById(PORTAL_HOST_ID)?.shadowRoot;
    if (portalShadowRoot) {
      portalShadowRoot.appendChild(iconsLink.cloneNode());
    }
  };

  const iconsLinkIds = ['eliberty-icons-default', 'eliberty-icons-theme'];
  const pendingIds = new Set<string>();

  for (const id of iconsLinkIds) {
    const existingLink = document.getElementById(id) as HTMLLinkElement;
    if (existingLink) {
      cloneIconsLinkToShadowRoots(existingLink);
    } else {
      pendingIds.add(id);
    }
  }

  if (pendingIds.size > 0) {
    const observer = new MutationObserver((_mutations, obs) => {
      for (const id of pendingIds) {
        const iconsLink = document.getElementById(id) as HTMLLinkElement;
        if (iconsLink) {
          cloneIconsLinkToShadowRoots(iconsLink);
          pendingIds.delete(id);
        }
      }
      if (pendingIds.size === 0) {
        obs.disconnect();
      }
    });
    observer.observe(document.head, { childList: true });
  }

  const rootElement = document.createElement('div');
  const rootId = `${widget.id}`;
  rootElement.setAttribute('id', rootId);
  host.shadowRoot!.appendChild(rootElement);

  // Defer rendering until all critical stylesheets are loaded.
  // This prevents any flash of unstyled content: if React hasn't rendered yet,
  // there is simply nothing to display.
  const doRender = () => {
    const root = createRoot(rootElement);
    root.render(
      <PostHogProvider apiKey={'phc_uHfq6FhyHEQMvYFL4Ta92HXStCa0OvXyizQT21M8gZ1'} options={options}>
        <ApolloProvider client={graphQlClient}>
          <Provider store={store}>
            <PersistGate persistor={persistor}>
              <ElibertyThemeProvider
                cssVariablesSelector={`#${rootId}`}
                getRootElement={() => rootElement}
                iconVersion='2.2'
                themeName={theme}
              >
                <IntlProvider locale={getLocale()} messages={loadTranslations(getLocale())}>
                  <ErrorBoundary>
                    <CacheVersionProvider>
                      <ConfiguratorProvider>
                        <HostElementProvider host={host}>
                          <widget.component />
                        </HostElementProvider>
                      </ConfiguratorProvider>
                    </CacheVersionProvider>
                  </ErrorBoundary>
                </IntlProvider>
              </ElibertyThemeProvider>
            </PersistGate>
          </Provider>
        </ApolloProvider>
      </PostHogProvider>,
    );

    if (!document.getElementById(PORTAL_HOST_ID)) {
      const portalHost = document.createElement('div');
      portalHost.id = PORTAL_HOST_ID;
      document.body.appendChild(portalHost);

      const portalShadowRoot = portalHost.attachShadow({ mode: 'open' });

      const portalContainer = document.createElement('div');
      portalContainer.id = PORTAL_HOST_ID;
      portalShadowRoot.append(portalContainer, style.cloneNode());
      if (variablesLinkElement) {
        portalShadowRoot.append(portalContainer, variablesLinkElement.cloneNode());
      }

      const portalRoot = createRoot(portalContainer);
      portalRoot.render(
        <ElibertyThemeProvider
          cssVariablesSelector={`#${PORTAL_HOST_ID}`}
          getRootElement={() => portalContainer}
          iconVersion='2.2'
          themeName={theme}
        >
          <div id={PORTAL_INNER_ID} style={{ left: 0, position: 'absolute', top: 0 }} />
        </ElibertyThemeProvider>,
      );
    }
  };

  const linkLoaded = (link: HTMLLinkElement) =>
    new Promise<void>((resolve) => {
      if (link.sheet) {
        resolve();
        return;
      }
      link.addEventListener('load', () => resolve(), { once: true });
      link.addEventListener('error', () => resolve(), { once: true });
    });

  const linksToWait = (
    [style, variablesLinkElement, ...fontLinksToWait] as Array<HTMLLinkElement | undefined>
  ).filter((l): l is HTMLLinkElement => !!l);

  void Promise.all(linksToWait.map(linkLoaded))
    .then(() => {
      [...document.fonts].forEach((face) => face.load().catch(() => undefined));
      return Promise.race([
        document.fonts.ready,
        new Promise<void>((resolve) => setTimeout(resolve, 2000)),
      ]);
    })
    .then(doRender);
};

export const createWidget = (widget: WidgetsInterface) => {
  class GenericWidgetElement extends HTMLElement {
    connectedCallback() {
      initWidgetShadowDom(this, widget);
    }
  }

  const tagName = `${widget.id}-widget`;
  if (!customElements.get(tagName)) {
    customElements.define(tagName, GenericWidgetElement);
  }
};
